/**
 * Helpers de Noticias (ACHIEVERY-1363, Etapa 3).
 * Funciones puras para mapear documentos `clc_news` de Prismic al shape que consumen
 * las cards (Home destacada + top 5, y la grilla del listado). Compartido por
 * NewsHome.vue, NewsGrid.vue y CLCNewsList.vue.
 */
import { getCleanPrismicImageUrl } from './prismicHelpers';

const MONTHS = {
  'en-us': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  'es-es': ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
};

// Traducción de categorías (mismas claves que `tagsBase.prismicCategory` en la home).
const CATEGORY_I18N = {
  'Events': { 'en-us': 'Events', 'es-es': 'Eventos' },
  'Studying tips': { 'en-us': 'Studying tips', 'es-es': 'Consejos de estudio' },
  'Partnership': { 'en-us': 'Partnerships', 'es-es': 'Alianzas' },
  'Partnerships': { 'en-us': 'Partnerships', 'es-es': 'Alianzas' },
  'Features': { 'en-us': 'Features', 'es-es': 'Funcionalidades' },
  'Success stories': { 'en-us': 'Success stories', 'es-es': 'Casos de éxito' },
  'Learning essentials': { 'en-us': 'Learning essentials', 'es-es': 'Recursos esenciales' }
};

// Tags/categorías para los filtros del listado (hardcodeadas, igual que la home legacy).
// `prismicCategory` = valor exacto que llega en item.data.categories[0].category.
export const NEWS_TAGS = [
  { id: 1, nameEN: 'Events', nameES: 'Eventos', prismicCategory: 'Events' },
  { id: 2, nameEN: 'Studying tips', nameES: 'Consejos de estudio', prismicCategory: 'Studying tips' },
  { id: 3, nameEN: 'Partnerships', nameES: 'Alianzas', prismicCategory: 'Partnerships' },
  { id: 4, nameEN: 'Features', nameES: 'Funcionalidades', prismicCategory: 'Features' },
  { id: 5, nameEN: 'Success stories', nameES: 'Casos de éxito', prismicCategory: 'Success stories' },
  { id: 6, nameEN: 'Learning essentials', nameES: 'Recursos esenciales', prismicCategory: 'Learning essentials' }
];

export function translateCategory(category, lang) {
  const entry = CATEGORY_I18N[category];
  if (entry) {
    return entry[lang] || entry['en-us'];
  }
  return category || '';
}

export function formatNewsDate(dateStr, lang) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const months = MONTHS[lang] || MONTHS['en-us'];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/**
 * Resuelve el destino de una noticia: link EXTERNO (campo nuevo de Prismic, a implementar)
 * o el detalle interno `/{lang}/news/{uid}`. Devuelve { href, target }.
 * TODO: confirmar el nombre del campo de link externo (`news_external_link`?).
 */
export function resolveNewsHref(item, lang, center) {
  const external = item?.data?.news_external_link?.url || '';
  if (external) {
    return { href: external, target: item?.data?.news_external_link?.target || '_blank' };
  }
  const l = lang === 'es-es' ? 'es' : 'en';
  const query = center ? `?center=${center}` : '';
  return { href: `/${l}/news/${item.uid}${query}`, target: '_self' };
}

/** Mapea un documento `clc_news` al shape de card. */
export function mapNewsItem(item, lang, center) {
  const rawCategory = item?.data?.categories?.[0]?.category || '';
  const link = resolveNewsHref(item, lang, center);
  return {
    id: item.uid,
    title: item?.data?.news_title?.[0]?.text || '',
    image: getCleanPrismicImageUrl(item?.data?.news_image?.url),
    imageAlt: item?.data?.news_image?.alt || '',
    excerpt: item?.data?.news_body?.[0]?.text || '',
    rawCategory,                                  // categoría cruda de Prismic (para filtrar)
    category: translateCategory(rawCategory, lang), // etiqueta traducida (para mostrar)
    date: formatNewsDate(item?.first_publication_date, lang),
    ribbon: item?.data?.ribbon || '',
    cta: item?.data?.news_link_text || (lang === 'en-us' ? 'Learn more' : 'Leer más'),
    // `featured` (booleano) en `clc_news`: marca la noticia como candidata a la posición destacada.
    // Entre las featured, la Home elige la de last_publication_date más reciente (ver NewsHome.vue).
    featured: item?.data?.featured === true,
    href: link.href,
    target: link.target
  };
}
